// fetchTrustPilotData fetches Trustpilot review data for a product based on its SKUs.
// It detects whether it's running in PLP or PDP context using flags.
// Based on the context, it builds the Trustpilot API URL with either all variant SKUs or colour-matched SKUs.
// It fetches the review summary, rounds the average rating, and maps it to a star SVG component.
// Returns the raw data, average rating, and corresponding star image to be displayed.
// Uses a switch statement to construct the API URL dynamically based on the page type (PLP or PDP).
// Ensures star ratings are visually represented using pre-imported SVGs.
// Handles API failures gracefully with error logging.

import { StringHelpers } from 'helpers/stringHelpers';
import OneStar from 'public/images/Trustpilot_ratings_1star-RGB.svg';
import OneAndHalf from 'public/images/Trustpilot_ratings_1halfstar-RGB.svg';
import TwoStar from 'public/images/Trustpilot_ratings_2star-RGB.svg';
import TwoAndHalf from 'public/images/Trustpilot_ratings_2halfstar-RGB.svg';
import ThreeStar from 'public/images/Trustpilot_ratings_3star-RGB.svg';
import ThreeAndHalf from 'public/images/Trustpilot_ratings_3halfstar-RGB.svg';
import FourStar from 'public/images/Trustpilot_ratings_4star-RGB.svg';
import FourAndHalf from 'public/images/Trustpilot_ratings_4halfstar-RGB.svg';
import FiveStar from 'public/images/FiveStar.svg';
import { Variant } from 'shared/types/product';
import { Product } from 'types/next';
import BlackFiveStar from 'public/images/Trustpilot_ratings_5star-RGB.svg';

export const fetchTrustPilotData = async (product: any, variant?: Variant, isPLP?: boolean, isPDP?: boolean) => {
  let url;

  const caseType = isPLP ? 'PLP' : isPDP ? 'PDP' : '';

  switch (caseType) {
    case 'PLP': {
      const skus = product?.variants
        .filter((v: any) => v?.sku)
        .map((v: any) => StringHelpers?.removeWhiteSpace(v?.sku))
        .join(',');

      url = `https://widget.trustpilot.com/trustbox-data/5717796816f630043868e2e8?businessUnitId=4851648a000064000502aefc&locale=en_GB&sku=(${encodeURIComponent(
        skus,
      )})`;
      break;
    }

    case 'PDP':
    default: {
      const swatchColorSkus = product?.variants
        ?.filter((v: any) => v?.attributes?.colour === variant?.attributes?.colour)
        .map((v: any) => StringHelpers.removeWhiteSpace(v?.sku))
        .join(',');

      url = `https://widget.trustpilot.com/trustbox-data/5717796816f630043868e2e8?businessUnitId=4851648a000064000502aefc&locale=en_GB&sku=(${encodeURIComponent(
        swatchColorSkus,
      )})`;
      break;
    }
  }

  try {
    const response = await fetch(url);

    if (!response?.ok) {
      console.error('HTTP error:', response?.status);
      return;
    }

    const data = await response?.json();

    const starsAverage = data?.productReviewsSummary?.starsAverage;
    const numberOfReviews = data?.productReviewsSummary?.numberOfReviews?.total;
    let StarImageComponent = null;
    if (starsAverage !== undefined) {
      const reviewStar = {
        '1': OneStar,
        '1.5': OneAndHalf,
        '2': TwoStar,
        '2.5': TwoAndHalf,
        '3': ThreeStar,
        '3.5': ThreeAndHalf,
        '4': FourStar,
        '4.5': FourAndHalf,
        '5': FiveStar,
      };

      function roundStarRating(value: number): number {
        return value === 5 ? 5 : Math.floor(value * 2) / 2;
      }

      const roundedStarValue = roundStarRating(starsAverage);
      if (roundedStarValue === 5) {
        if (numberOfReviews >= 50) {
          StarImageComponent = FiveStar;
        } else {
          StarImageComponent = BlackFiveStar;
        }
      } else {
        StarImageComponent = (reviewStar as any)[roundedStarValue];
      }
    }

    return {
      raw: data,
      starsAverage,
      StarImageComponent,
      numberOfReviews,
    };
  } catch (error) {
    console.error('Failed to fetch Trustpilot data', error);
  }
};
